import React, { useState, useEffect, useCallback, useRef } from 'react';
import toast from 'react-hot-toast';
import {
  Send, Plus, Copy, Loader2, Check, Trash2, MessageSquare, PanelLeft, PenSquare,
  Globe, Brain, Database, ExternalLink, ChevronDown, ChevronRight,
} from 'lucide-react';
import Button from './ui/Button';
import { AnimatedModal } from './AnimatedComponents';
import RispostaCopywriter from './RispostaCopywriter';
import ApprendimentoAssistente from './ApprendimentoAssistente';

interface WebSource {
  url: string;
  title: string;
}

interface Message {
  role: 'user' | 'assistant';
  content: string;
  sources?: WebSource[];
  searched?: boolean;
  deep?: boolean;
}

interface ChatSummary {
  id: string;
  title: string;
  updated_at: string;
  message_count: number;
}

/** Rubrica o format ricorrente del cliente: la descrizione sono le sue linee guida. */
interface Rubrica {
  id: string;
  tipo: 'rubrica' | 'format';
  titolo: string;
  descrizione: string | null;
  gancio?: string | null;
  corpo?: string | null;
  cta?: string | null;
}

/** La rubrica ha una struttura gancio/corpo/CTA fissata dal copywriter. */
const haStruttura = (r: Rubrica) => Boolean(r.gancio || r.corpo || r.cta);

/** Brief creative del cliente (tab "Linee Guida" del Copywriter). */
interface Creative {
  obiettivi: string | null;
  taglio: string | null;
  da_dire: string | null;
  da_non_dire: string | null;
  note: string | null;
}

/** Una sezione del prompt (regole) o del contesto (fonti), come la legge l'AI. */
interface BrainSection {
  titolo: string;
  testo: string;
}

/** Regole + fonti dell'assistente per questo cliente. */
interface Brain {
  clientName: string;
  modello: string;
  modelloApprofondito: string;
  contextUsed: ContextUsed;
  caratteriContesto: number;
  tokenStimati: number;
  regole: BrainSection[];
  fonti: BrainSection[];
  blocchi: BrainBlocco[];
}

/** Un blocco di lavoro dell'assistente (vedi CopywriterService.describeBrain) */
interface BrainBlocco {
  id: string;
  titolo: string;
  descrizione: string;
  stato: string;
  attivo: boolean;
  sezioni: BrainSection[];
}

/** Fonti del cliente su cui l'assistente sta effettivamente lavorando. */
interface ContextUsed {
  reels: number;
  rubriche: number;
  news: number;
  hasCreative: boolean;
  contenuti: number;
  reelConMetriche: number;
  /** Regole imparate dal copywriter e attive nel prompt di questo cliente. */
  apprendimenti: number;
  /** Reel dei competitor con script trascritto che l'assistente sta leggendo. */
  competitor: number;
}

interface Props {
  clientId: string;
  clientName: string;
  getToken: () => string | null;
  onDraftSaved?: () => void;
  /** true = riempie il contenitore (modalità a tutto schermo del Copywriting) */
  fullscreen?: boolean;
  /** Logo del cliente in testata al posto dell'icona dell'assistente */
  clientLogoUrl?: string;
}

const SUGGERIMENTI = [
  'Scrivimi 3 reel per la prossima settimana',
  'Un reel che risponde a un\'obiezione tipica dei clienti',
  'Una didascalia per un post che racconta il dietro le quinte',
  'Un contenuto che riprende una delle nostre rubriche',
];

/** Data leggibile per la cronologia: oggi / ieri / data breve. */
const formatWhen = (iso: string): string => {
  const d = new Date(iso);
  const oggi = new Date();
  const ieri = new Date();
  ieri.setDate(oggi.getDate() - 1);
  const sameDay = (a: Date, b: Date) => a.toDateString() === b.toDateString();
  if (sameDay(d, oggi)) return d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
  if (sameDay(d, ieri)) return 'ieri';
  return d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' });
};

const CopywriterAssistant: React.FC<Props> = ({ clientId, clientName, getToken, onDraftSaved, fullscreen = false, clientLogoUrl }) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [savingIndex, setSavingIndex] = useState<number | null>(null);
  const [savedIndexes, setSavedIndexes] = useState<Set<number>>(new Set());
  const [deepThinking, setDeepThinking] = useState(false);

  // Cresce a ogni risposta: la pagina "Apprendimento" lo usa per ricontrollare
  // se da quello scambio è nata una regola nuova.
  const [scambiConclusi, setScambiConclusi] = useState(0);

  // cronologia
  const [chats, setChats] = useState<ChatSummary[]>([]);
  const [currentChatId, setCurrentChatId] = useState<string | null>(null);
  const [showHistory, setShowHistory] = useState(true);

  // rubriche del cliente: cosa c'è da produrre, con le relative linee guida
  const [rubriche, setRubriche] = useState<Rubrica[]>([]);
  const [rubricaAperta, setRubricaAperta] = useState<string | null>(null);

  // linee guida creative del cliente (obiettivi, taglio, cosa dire, cosa non dire, note)
  const [creative, setCreative] = useState<Creative | null>(null);

  // su quali fonti del cliente sta lavorando l'assistente (archivio, reel, performance)
  const [contesto, setContesto] = useState<ContextUsed | null>(null);

  // vista "cervello": regole e fonti dell'assistente, lette dal prompt reale
  const [brainAperto, setBrainAperto] = useState(false);
  const [brain, setBrain] = useState<Brain | null>(null);
  const [brainInCaricamento, setBrainInCaricamento] = useState(false);
  // "La mia memoria": blocco aperto e, dentro, sezione del prompt aperta
  const [bloccoAperto, setBloccoAperto] = useState<string | null>(null);
  const [sezioneAperta, setSezioneAperta] = useState<string | null>(null);
  const [lineaAperta, setLineaAperta] = useState<string | null>(null);

  const endRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const authHeaders = useCallback(
    () => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` }),
    [getToken]
  );

  // Nel browser teniamo solo il testo non ancora inviato e quale chat era aperta:
  // le conversazioni vere stanno sul server.
  const draftKey = `bymarke.copywriter.draft.${clientId}`;
  const hydratedFor = useRef<string | null>(null);

  const loadChats = useCallback(async (): Promise<ChatSummary[]> => {
    try {
      const res = await fetch(`/api/ai/chats?client_id=${clientId}`, { headers: authHeaders() });
      const data = await res.json();
      const list: ChatSummary[] = data.success ? data.data || [] : [];
      setChats(list);
      return list;
    } catch {
      return [];
    }
  }, [clientId, authHeaders]);

  const loadRubriche = useCallback(async () => {
    try {
      const res = await fetch(`/api/rubriche?client_id=${clientId}`, { headers: authHeaders() });
      const data = await res.json();
      setRubriche(data.success ? data.data || [] : []);
    } catch {
      setRubriche([]);
    }
  }, [clientId, authHeaders]);

  const loadContesto = useCallback(async () => {
    try {
      const res = await fetch(`/api/ai/context?client_id=${clientId}`, { headers: authHeaders() });
      const data = await res.json();
      setContesto(data.success ? data.data : null);
    } catch {
      setContesto(null);
    }
  }, [clientId, authHeaders]);

  /**
   * Apre il "cervello": regole e fonti arrivano dal prompt reale, quindi
   * si caricano all'apertura (e a ogni riapertura, così restano aggiornate
   * dopo una modifica alle linee guida o l'arrivo di nuovi dati).
   */
  const apriBrain = useCallback(async () => {
    setBrainAperto(true);
    setBrainInCaricamento(true);
    try {
      const res = await fetch(`/api/ai/brain?client_id=${clientId}`, { headers: authHeaders() });
      const data = await res.json();
      setBrain(data.success ? data.data : null);
      if (!data.success) toast.error(data.message || 'Non riesco a leggere le linee guida');
    } catch {
      setBrain(null);
      toast.error('Errore di rete');
    } finally {
      setBrainInCaricamento(false);
    }
  }, [clientId, authHeaders]);

  const loadCreative = useCallback(async () => {
    try {
      const res = await fetch(`/api/creative?client_id=${clientId}`, { headers: authHeaders() });
      const data = await res.json();
      setCreative(data.success ? data.data : null);
    } catch {
      setCreative(null);
    }
  }, [clientId, authHeaders]);

  const openChat = useCallback(
    async (chatId: string) => {
      try {
        const res = await fetch(`/api/ai/chats/${chatId}`, { headers: authHeaders() });
        const data = await res.json();
        if (data.success) {
          setMessages(Array.isArray(data.data.messages) ? data.data.messages : []);
          setCurrentChatId(chatId);
          setSavedIndexes(new Set());
        }
      } catch {
        toast.error('Impossibile aprire la conversazione');
      }
    },
    [authHeaders]
  );

  // All'apertura: carica la cronologia e riapre l'ultima conversazione usata.
  useEffect(() => {
    let annullato = false;
    (async () => {
      let draft = '';
      let lastChatId: string | null = null;
      try {
        const raw = localStorage.getItem(draftKey);
        const saved = raw ? JSON.parse(raw) : null;
        draft = typeof saved?.input === 'string' ? saved.input : '';
        lastChatId = typeof saved?.chatId === 'string' ? saved.chatId : null;
      } catch {
        /* niente bozza salvata */
      }

      const [list] = await Promise.all([loadChats(), loadRubriche(), loadCreative(), loadContesto()]);
      if (annullato) return;

      setInput(draft);
      if (lastChatId && list.some((c) => c.id === lastChatId)) {
        await openChat(lastChatId);
      } else {
        setMessages([]);
        setCurrentChatId(null);
        setSavedIndexes(new Set());
      }
      hydratedFor.current = clientId;
    })();
    return () => {
      annullato = true;
    };
  }, [clientId, draftKey, loadChats, loadRubriche, loadCreative, loadContesto, openChat]);

  // Salva la bozza non inviata e quale chat è aperta.
  useEffect(() => {
    if (hydratedFor.current !== clientId) return;
    try {
      localStorage.setItem(draftKey, JSON.stringify({ input, chatId: currentChatId }));
    } catch {
      /* storage pieno o non disponibile */
    }
  }, [input, currentChatId, clientId, draftKey]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages, isSending]);

  /** Salva la conversazione sul server (creandola se è la prima battuta). */
  const persist = async (msgs: Message[], chatId: string | null): Promise<string | null> => {
    try {
      if (!chatId) {
        const res = await fetch('/api/ai/chats', {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify({ client_id: clientId, messages: msgs }),
        });
        const data = await res.json();
        if (data.success) return data.data.id as string;
        return null;
      }
      await fetch(`/api/ai/chats/${chatId}`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ messages: msgs }),
      });
      return chatId;
    } catch {
      return chatId;
    }
  };

  const send = async (text?: string) => {
    const content = (text ?? input).trim();
    if (!content || isSending) return;

    const next: Message[] = [...messages, { role: 'user', content }];
    setMessages(next);
    setInput('');
    setIsSending(true);

    try {
      // La conversazione nasce PRIMA della risposta: l'apprendimento del primo
      // scambio (che parte sul server appena risponde) deve poter citare la
      // chat da cui viene. Creandola dopo, le regole imparate al primo
      // messaggio restavano senza origine (13 su 59 al 17/09/2026).
      let chatId = currentChatId;
      if (!chatId) {
        chatId = await persist(next, null);
        if (chatId) setCurrentChatId(chatId);
      }

      const res = await fetch('/api/ai/chat', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          client_id: clientId,
          // all'AI serve solo la conversazione: fonti e flag restano lato interfaccia
          messages: next.map((m) => ({ role: m.role, content: m.content })),
          deep_thinking: deepThinking,
          // Serve solo a legare alla conversazione ciò che l'assistente impara
          // da questo scambio.
          chat_id: chatId,
        }),
      });
      const data = await res.json();

      if (data.success) {
        const completo: Message[] = [
          ...next,
          {
            role: 'assistant',
            content: data.reply,
            sources: data.sources || [],
            searched: !!data.searched,
            deep: deepThinking,
          },
        ];
        setMessages(completo);
        const id = await persist(completo, chatId);
        if (id && id !== chatId) setCurrentChatId(id);
        await loadChats();
        setScambiConclusi((n) => n + 1);
      } else {
        toast.error(data.message || 'Errore nella risposta');
        setMessages(next); // il messaggio dell'utente resta, così può riprovare
      }
    } catch {
      toast.error('Errore di rete');
    } finally {
      setIsSending(false);
      inputRef.current?.focus();
    }
  };

  const newChat = () => {
    setMessages([]);
    setCurrentChatId(null);
    setSavedIndexes(new Set());
    setInput('');
    inputRef.current?.focus();
  };

  /**
   * Apre una chat nuova con la richiesta già scritta per quella rubrica.
   * Il testo non viene inviato: chi scrive può aggiungere il taglio che vuole
   * prima di far partire la generazione. Le linee guida non le ripetiamo nel
   * messaggio, l'assistente ha già le rubriche del cliente nel suo contesto.
   */
  const chatSuRubrica = (r: Rubrica) => {
    setMessages([]);
    setCurrentChatId(null);
    setSavedIndexes(new Set());
    setInput(
      `Scrivimi un contenuto per ${r.tipo === 'format' ? 'il format' : 'la rubrica'} «${r.titolo}»`
    );
    inputRef.current?.focus();
  };

  /**
   * Le linee guida del cliente come voci apribili: mostriamo solo quelle
   * compilate nella tab "Linee Guida" del Copywriter.
   */
  const vociLineeGuida = [
    { chiave: 'obiettivi', etichetta: 'Obiettivi di comunicazione', testo: creative?.obiettivi, classe: 'text-slate-700 dark:text-slate-200' },
    { chiave: 'taglio', etichetta: 'Taglio e stile', testo: creative?.taglio, classe: 'text-slate-700 dark:text-slate-200' },
    { chiave: 'da_dire', etichetta: 'Cosa valorizzare', testo: creative?.da_dire, classe: 'text-emerald-600 dark:text-emerald-400' },
    { chiave: 'da_non_dire', etichetta: 'Cosa evitare', testo: creative?.da_non_dire, classe: 'text-red-500 dark:text-red-400' },
    { chiave: 'note', etichetta: 'Note', testo: creative?.note, classe: 'text-slate-700 dark:text-slate-200' },
  ].filter((v): v is { chiave: string; etichetta: string; testo: string; classe: string } =>
    Boolean(v.testo && v.testo.trim())
  );

  const deleteChat = async (chatId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm('Eliminare questa conversazione?')) return;
    try {
      const res = await fetch(`/api/ai/chats/${chatId}`, { method: 'DELETE', headers: authHeaders() });
      const data = await res.json();
      if (data.success) {
        if (chatId === currentChatId) newChat();
        await loadChats();
        toast.success('Conversazione eliminata');
      } else {
        toast.error(data.message || 'Eliminazione fallita');
      }
    } catch {
      toast.error('Errore di rete');
    }
  };

  const addToContents = async (text: string, index: number) => {
    setSavingIndex(index);
    try {
      const res = await fetch('/api/ai/save-from-chat', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ client_id: clientId, text }),
      });
      const data = await res.json();
      if (data.success) {
        // La risposta può contenere più proposte: diventano una bozza ciascuna.
        const quanti = data.count ?? 1;
        toast.success(
          quanti > 1 ? `${quanti} script aggiunti agli Script Attuali` : 'Aggiunto agli Script Attuali'
        );
        setSavedIndexes((prev) => new Set(prev).add(index));
        onDraftSaved?.();
      } else {
        // Con un errore di validazione il messaggio generico ("Validation failed")
        // non dice nulla a chi scrive: mostriamo il dettaglio del campo.
        const dettaglio = data.errors?.[0]?.message;
        toast.error(dettaglio || data.message || 'Salvataggio fallito');
      }
    } catch {
      toast.error('Errore di rete durante il salvataggio');
    } finally {
      setSavingIndex(null);
    }
  };

  const copy = (text: string) => {
    navigator.clipboard.writeText(text).then(
      () => toast.success('Copiato'),
      () => toast.error('Copia non riuscita')
    );
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <div className="flex gap-4" style={fullscreen ? { height: '100%', minHeight: 0 } : { height: 'calc(100vh - 300px)', minHeight: 440 }}>
      {/* Cronologia */}
      {showHistory && (
        <aside className="hidden md:flex w-60 flex-shrink-0 flex-col border-r border-slate-200 dark:border-gray-700 pr-3">
          <button
            onClick={newChat}
            className="inline-flex items-center justify-center gap-2 w-full px-3 py-2 rounded-xl text-sm font-medium border border-slate-200 dark:border-gray-700 text-slate-700 dark:text-slate-200 hover:border-[#fdf751] hover:bg-[#fdf751]/10 transition-colors"
          >
            <PenSquare size={15} /> Nuova chat
          </button>

          <div className="mt-3 flex-1 overflow-y-auto space-y-0.5">
            {/* Rubriche del cliente: cosa c'è da produrre, con le sue linee guida */}
            <p className="px-2 pt-1 pb-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
              Rubriche
            </p>
            {rubriche.length === 0 ? (
              <p className="text-[11px] text-slate-400 px-2 pb-3">
                Nessuna rubrica impostata per {clientName}.
              </p>
            ) : (
              <div className="pb-2 space-y-0.5">
                {rubriche.map((r) => {
                  const aperta = rubricaAperta === r.id;
                  return (
                    <div key={r.id}>
                      <div className="group flex items-start gap-1 px-1.5 py-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-gray-700 transition-colors">
                        <button
                          onClick={() =>
                            setRubricaAperta((corrente) => (corrente === r.id ? null : r.id))
                          }
                          className={`flex-shrink-0 mt-0.5 text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 transition-colors ${
                            r.descrizione || haStruttura(r) ? '' : 'invisible'
                          }`}
                          title={aperta ? 'Nascondi le linee guida' : 'Mostra le linee guida'}
                          aria-expanded={aperta}
                        >
                          {aperta ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                        </button>
                        <button
                          onClick={() => chatSuRubrica(r)}
                          className="min-w-0 flex-1 text-left"
                          title={`Scrivi un contenuto per «${r.titolo}»`}
                        >
                          <span className="block text-xs text-slate-700 dark:text-slate-200 leading-snug group-hover:text-slate-900 dark:group-hover:text-white">
                            {r.titolo}
                          </span>
                          {r.tipo === 'format' && (
                            <span className="text-[10px] text-slate-400">format</span>
                          )}
                        </button>
                      </div>
                      {aperta && r.descrizione && (
                        <p className="ml-6 mr-1.5 mb-1.5 text-[11px] leading-relaxed text-slate-500 dark:text-slate-400 whitespace-pre-wrap">
                          {r.descrizione}
                        </p>
                      )}
                      {aperta && haStruttura(r) && (
                        <div className="ml-6 mr-1.5 mb-1.5 space-y-0.5 text-[11px] leading-relaxed text-slate-500 dark:text-slate-400">
                          {([['Gancio', r.gancio], ['Corpo', r.corpo], ['CTA', r.cta]] as const)
                            .filter(([, testo]) => testo)
                            .map(([etichetta, testo]) => (
                              <p key={etichetta} className="whitespace-pre-wrap">
                                <span className="font-semibold uppercase tracking-wide text-[9px] text-slate-400 mr-1">{etichetta}</span>
                                {testo}
                              </p>
                            ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Linee guida creative del cliente, stesso toggle delle rubriche */}
            <p className="px-2 pt-2 pb-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-400 border-t border-slate-200 dark:border-gray-700">
              Linee guida
            </p>
            {vociLineeGuida.length === 0 ? (
              <p className="text-[11px] text-slate-400 px-2 pb-3">
                Nessuna linea guida impostata per {clientName}.
              </p>
            ) : (
              <div className="pb-2 space-y-0.5">
                {vociLineeGuida.map((v) => {
                  const aperta = lineaAperta === v.chiave;
                  return (
                    <div key={v.chiave}>
                      <button
                        onClick={() =>
                          setLineaAperta((corrente) => (corrente === v.chiave ? null : v.chiave))
                        }
                        className="group w-full flex items-start gap-1 px-1.5 py-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-gray-700 transition-colors text-left"
                        aria-expanded={aperta}
                      >
                        <span className="flex-shrink-0 mt-0.5 text-slate-400 group-hover:text-slate-700 dark:group-hover:text-slate-200 transition-colors">
                          {aperta ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                        </span>
                        <span className={`min-w-0 flex-1 text-xs leading-snug ${v.classe}`}>
                          {v.etichetta}
                        </span>
                      </button>
                      {aperta && (
                        <p className="ml-6 mr-1.5 mb-1.5 text-[11px] leading-relaxed text-slate-500 dark:text-slate-400 whitespace-pre-wrap">
                          {v.testo}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {chats.length > 0 && (
              <p className="px-2 pt-2 pb-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-400 border-t border-slate-200 dark:border-gray-700">
                Conversazioni
              </p>
            )}
            {chats.map((c) => (
              <div
                key={c.id}
                onClick={() => openChat(c.id)}
                className={`group flex items-center gap-2 px-2.5 py-2 rounded-lg cursor-pointer transition-colors ${
                  c.id === currentChatId
                    ? 'bg-[#fdf751]/20 border border-[#fdf751]/50'
                    : 'hover:bg-slate-100 dark:hover:bg-gray-700 border border-transparent'
                }`}
              >
                <MessageSquare size={13} className="flex-shrink-0 text-slate-400" />
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-slate-700 dark:text-slate-200 truncate">{c.title}</p>
                  <p className="text-[10px] text-slate-400">{formatWhen(c.updated_at)}</p>
                </div>
                <button
                  onClick={(e) => deleteChat(c.id, e)}
                  className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-500 transition-opacity flex-shrink-0"
                  title="Elimina conversazione"
                >
                  <Trash2 size={13} />
                </button>
              </div>
            ))}
          </div>
        </aside>
      )}

      {/* Chat */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Intestazione */}
        <div className="flex items-center justify-between gap-2 pb-3 border-b border-slate-200 dark:border-gray-700">
          {/* su mobile i chip vanno a capo invece di finire sotto "Nuova" */}
          <div className="flex flex-wrap items-center gap-2 sm:gap-3 min-w-0 flex-1">
            <button
              onClick={() => setShowHistory((s) => !s)}
              className="hidden md:inline-flex p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 dark:hover:bg-gray-700"
              title={showHistory ? 'Nascondi cronologia' : 'Mostra cronologia'}
            >
              <PanelLeft size={16} />
            </button>
            {/* a tutto schermo la barra in alto ha già titolo e cliente: su mobile non li ripetiamo */}
            {/* Logo del cliente (o la sua iniziale): è di lui che si parla, non dell'AI */}
            <div className={`flex-shrink-0 w-10 h-10 rounded-full overflow-hidden ${fullscreen ? 'hidden md:block' : ''}`} style={{ backgroundColor: '#F4F5F7', border: '1px solid #EBEDF1' }}>
              {clientLogoUrl ? (
                <img src={clientLogoUrl} alt={clientName} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-sm font-medium" style={{ color: '#8B92A0' }}>
                  {clientName?.charAt(0)?.toUpperCase() || '?'}
                </div>
              )}
            </div>
            <h3 className={`font-semibold text-slate-900 dark:text-slate-100 truncate ${fullscreen ? 'hidden md:block' : ''}`}>
              {clientName} - AI Assistant
            </h3>
            {/* Cosa c'è dentro l'assistente: regole che segue e fonti che legge */}
            <button
              onClick={apriBrain}
              title="Cosa ha in memoria per questo cliente: regole, fonti e dati"
              aria-label="La mia memoria"
              className="flex-shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium whitespace-nowrap border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-slate-500 dark:text-slate-400 hover:border-[#fdf751] hover:text-slate-800 dark:hover:text-slate-100 hover:bg-[#fdf751]/10 transition-colors"
            >
              <Database size={13} />
              La mia memoria
            </button>
            {/* Accanto a "come ragiono" (cosa sa di partenza) c'è "Apprendimento":
                cosa ha imparato da te strada facendo. */}
            <ApprendimentoAssistente
              clientId={clientId}
              clientName={clientName}
              getToken={getToken}
              segnaleNuovoScambio={scambiConclusi}
            />
          </div>
          <button
            onClick={newChat}
            className="md:hidden inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-slate-800 flex-shrink-0 whitespace-nowrap"
          >
            <PenSquare size={13} /> Nuova
          </button>
        </div>

        {/* Conversazione */}
        <div className="flex-1 overflow-y-auto py-4 space-y-4">
          {messages.length === 0 && (
            <div className="h-full flex flex-col items-center justify-center text-center px-4">
              <p className="text-sm text-slate-500 dark:text-slate-400 max-w-md">
                Chiedi quello che ti serve. Conosco già la voce di <b>{clientName}</b> dalla mia memoria.
                Dimmi cosa ti serve: ti aiuto a perfezionarlo o ti suggerisco qualcosa.
              </p>

              {/* Cosa sto davvero leggendo di questo cliente: niente promesse a vuoto */}
              {contesto && (
                <div className="mt-3 flex flex-wrap justify-center gap-1.5 max-w-xl">
                  {[
                    contesto.contenuti > 0 && `${contesto.contenuti} contenuti in archivio`,
                    contesto.reels > 0 && `${contesto.reels} reel trascritti`,
                    contesto.reelConMetriche > 0 && `${contesto.reelConMetriche} reel con performance`,
                    contesto.rubriche > 0 && `${contesto.rubriche} rubriche`,
                    contesto.apprendimenti > 0 &&
                      `${contesto.apprendimenti} ${contesto.apprendimenti === 1 ? 'cosa imparata da te' : 'cose imparate da te'}`,
                    contesto.competitor > 0 && `${contesto.competitor} reel dei competitor`,
                    contesto.hasCreative && 'linee guida',
                  ]
                    .filter(Boolean)
                    .map((v) => (
                      <span
                        key={String(v)}
                        className="px-2 py-0.5 rounded-full text-[11px] bg-slate-100 dark:bg-gray-700 text-slate-500 dark:text-slate-400"
                      >
                        {v}
                      </span>
                    ))}
                </div>
              )}
              <div className="mt-4 flex flex-wrap justify-center gap-2 max-w-xl">
                {SUGGERIMENTI.map((s) => (
                  <button
                    key={s}
                    onClick={() => send(s)}
                    className="px-3 py-1.5 rounded-full text-xs border border-slate-200 dark:border-gray-700 text-slate-600 dark:text-slate-300 hover:border-[#fdf751] hover:bg-[#fdf751]/10 transition-colors"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((m, i) =>
            m.role === 'user' ? (
              <div key={i} className="flex justify-end">
                <div className="max-w-[85%] rounded-2xl rounded-br-md px-4 py-2.5 bg-[#fdf751]/25 border border-[#fdf751]/50">
                  <p className="text-sm text-slate-800 dark:text-slate-100 whitespace-pre-wrap">{m.content}</p>
                </div>
              </div>
            ) : (
              <div key={i} className="flex justify-start">
                <div className="max-w-[92%] w-full">
                  <div className="rounded-2xl rounded-bl-md px-4 py-3 bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-700">
                    {(m.searched || m.deep) && (
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        {m.searched && (
                          <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-sky-50 text-sky-700 border border-sky-200">
                            <Globe size={10} /> verificato sul web
                          </span>
                        )}
                        {m.deep && (
                          <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-violet-50 text-violet-700 border border-violet-200">
                            <Brain size={10} /> ragionamento profondo
                          </span>
                        )}
                      </div>
                    )}

                    <RispostaCopywriter testo={m.content} />

                    {m.sources && m.sources.length > 0 && (
                      <div className="mt-3 pt-2 border-t border-slate-100 dark:border-gray-700">
                        <p className="text-[10px] uppercase tracking-wide font-semibold text-slate-400 mb-1">Fonti</p>
                        <div className="space-y-0.5">
                          {m.sources.slice(0, 5).map((s) => (
                            <a
                              key={s.url}
                              href={s.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-1 text-[11px] text-slate-500 hover:text-sky-600 truncate"
                            >
                              <ExternalLink size={10} className="flex-shrink-0" />
                              <span className="truncate">{s.title}</span>
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-1.5 ml-1">
                    <Button
                      size="sm"
                      variant={savedIndexes.has(i) ? 'secondary' : 'primary'}
                      icon={savingIndex === i ? Loader2 : savedIndexes.has(i) ? Check : Plus}
                      disabled={savingIndex === i || savedIndexes.has(i)}
                      onClick={() => addToContents(m.content, i)}
                    >
                      {savedIndexes.has(i) ? 'Aggiunto ai contenuti' : 'Aggiungi ai contenuti'}
                    </Button>
                    <button
                      onClick={() => copy(m.content)}
                      className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-gray-700"
                    >
                      <Copy size={13} /> Copia
                    </button>
                  </div>
                </div>
              </div>
            )
          )}

          {isSending && (
            <div className="flex justify-start">
              <div className="rounded-2xl rounded-bl-md px-4 py-3 bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-700">
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: '0s' }} />
                    <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: '0.15s' }} />
                    <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: '0.3s' }} />
                  </div>
                  {deepThinking && (
                    <span className="text-[11px] text-violet-600">sto ragionando…</span>
                  )}
                </div>
              </div>
            </div>
          )}
          <div ref={endRef} />
        </div>

        {/* Barra di scrittura */}
        <div className="pt-3 border-t border-slate-200 dark:border-gray-700">
          <div className="flex items-end gap-2">
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              rows={2}
              placeholder={`Scrivi cosa ti serve per ${clientName}…`}
              className="flex-1 resize-none rounded-xl border border-slate-200 dark:border-gray-700 dark:bg-gray-800 dark:text-slate-100 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#fdf751]"
            />
            <Button
              variant="primary"
              icon={isSending ? Loader2 : Send}
              disabled={isSending || !input.trim()}
              onClick={() => send()}
            >
              Invia
            </Button>
          </div>
          <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
            <button
              onClick={() => setDeepThinking((v) => !v)}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium border transition-colors ${
                deepThinking
                  ? 'bg-violet-50 border-violet-300 text-violet-700'
                  : 'bg-white dark:bg-gray-800 border-slate-200 dark:border-gray-700 text-slate-500 hover:border-violet-300'
              }`}
              title="Pensa più a lungo prima di rispondere: meglio per strategie e piani editoriali, ma più lento"
            >
              <Brain size={12} />
              Ragionamento profondo
              {deepThinking && <Check size={11} />}
            </button>
            <p className="text-[11px] text-slate-400">
              {deepThinking
                ? 'Risposte più ragionate: possono richiedere fino a un minuto'
                : 'Invio per inviare · Shift+Invio per andare a capo'}
            </p>
          </div>
        </div>
      </div>

      {/* Il "cervello": regole che segue e fonti che legge, prese dal prompt vero */}
      <AnimatedModal
        isOpen={brainAperto}
        onClose={() => setBrainAperto(false)}
        title={`Cosa ha in memoria l'assistente · ${clientName}`}
        size="xl"
      >
        {brainInCaricamento && !brain ? (
          <div className="py-10 text-center">
            <Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-400" />
            <p className="mt-2 text-sm text-slate-500">Leggo regole e fonti…</p>
          </div>
        ) : !brain ? (
          <p className="py-8 text-center text-sm text-slate-500">
            Non riesco a leggere le linee guida di questo cliente.
          </p>
        ) : (
          <div className="space-y-4">
            <p className="text-xs sm:text-sm text-slate-500">
              Per scrivere un contenuto di {clientName} l'assistente tiene in memoria queste informazioni e le usa tutte insieme,
              scegliendo di volta in volta cosa pesa di più: non sono passaggi da eseguire in fila.
              Apri un blocco per vedere cosa contiene oggi e il testo esatto che riceve.
            </p>

            {/* I blocchi di lavoro, piatti: numero, titolo, stato; aperti mostrano
                la descrizione e le sezioni del prompt che ci ricadono */}
            <div className="max-h-[60vh] overflow-y-auto pr-1" style={{ borderTop: '1px solid #EBEDF1' }}>
              {brain.blocchi.filter((b) => b.id !== 'altro').map((b) => {
                const aperto = bloccoAperto === b.id;
                const principale = b.id !== 'altro';
                return (
                  <div key={b.id} style={{ borderBottom: '1px solid #EBEDF1' }}>
                    <button
                      onClick={() => { setBloccoAperto(aperto ? null : b.id); setSezioneAperta(null); }}
                      className="w-full flex items-start gap-3 py-3 text-left"
                    >
                      <span
                        className="flex-shrink-0 w-6 text-right tabular-nums pt-0.5"
                        style={{ fontSize: 9, color: principale ? '#8B92A0' : '#DDE0E6' }}
                      >
                        {principale ? '●' : '·'}
                      </span>
                      <span className="flex-1 min-w-0">
                        <span className={`block text-sm ${principale ? 'font-semibold' : 'font-medium'}`} style={{ color: b.attivo ? '#13161C' : '#8B92A0' }}>
                          {b.titolo}
                        </span>
                        <span className="block text-[11px] sm:text-xs mt-0.5" style={{ color: b.attivo ? '#5B6270' : '#B45309' }}>
                          {b.stato}
                        </span>
                      </span>
                      <ChevronRight
                        size={16}
                        strokeWidth={1.75}
                        className="flex-shrink-0 mt-0.5 transition-transform"
                        style={{ color: '#8B92A0', transform: aperto ? 'rotate(90deg)' : 'none' }}
                      />
                    </button>

                    {aperto && (
                      <div className="pl-9 pb-4 space-y-3">
                        <p className="text-xs sm:text-sm leading-relaxed" style={{ color: '#5B6270' }}>{b.descrizione}</p>
                        {b.sezioni.length === 0 ? (
                          <p className="text-xs" style={{ color: '#B45309' }}>
                            Per questo cliente il blocco è vuoto: va compilato nella scheda cliente o nel Copywriting.
                          </p>
                        ) : (
                          <div className="space-y-1">
                            {b.sezioni.map((s) => {
                              const aperta = sezioneAperta === s.titolo;
                              return (
                                <div key={s.titolo}>
                                  <button
                                    onClick={() => setSezioneAperta(aperta ? null : s.titolo)}
                                    className="w-full flex items-center gap-2 py-1.5 text-left"
                                  >
                                    <ChevronRight size={13} className="flex-shrink-0 transition-transform" style={{ color: '#8B92A0', transform: aperta ? 'rotate(90deg)' : 'none' }} />
                                    <span className="flex-1 min-w-0 text-xs sm:text-sm truncate" style={{ color: '#13161C' }}>{s.titolo}</span>
                                    <span className="flex-shrink-0 text-[10px] tabular-nums" style={{ color: '#8B92A0' }}>
                                      {s.testo.length.toLocaleString('it-IT')} caratteri
                                    </span>
                                  </button>
                                  {aperta && (
                                    <pre className="ml-5 mb-2 pl-3 text-[11px] leading-relaxed whitespace-pre-wrap break-words font-sans" style={{ color: '#5B6270', borderLeft: '2px solid #EBEDF1' }}>
                                      {s.testo}
                                    </pre>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <p className="text-[11px]" style={{ color: '#8B92A0' }}>
              Modello {brain.modello} (ragionamento profondo: {brain.modelloApprofondito}) ·
              {' '}~{brain.tokenStimati.toLocaleString('it-IT')} token di istruzioni e dati a ogni messaggio
            </p>
          </div>
        )}
      </AnimatedModal>
    </div>
  );
};

export default CopywriterAssistant;
